/**
 * @plerity/auth JWT Implementation
 *
 * JWT token generation and verification using jose library.
 * Compatible with Cloudflare Workers (no Node.js APIs).
 */

import { errors, jwtVerify, SignJWT } from "jose";
import type {
  AuthConfig,
  AuthUser,
  RefreshTokenPayload,
  RefreshVerifyResult,
  TokenPair,
  TokenPayload,
  UnifiedClaims,
  UserClaims,
  VerifyResult,
} from "./types.js";

/** Token issuer identifier */
const TOKEN_ISSUER = "animas:portal";

/** Token audience identifier */
const TOKEN_AUDIENCE = "animas:all";

/**
 * Encode the JWT secret for use with jose.
 */
function getSecretKey(secret: string): Uint8Array {
  return new TextEncoder().encode(secret);
}

/**
 * Generate a signed access token with user claims.
 *
 * @param claims - User identity claims to embed
 * @param config - Auth configuration
 * @returns Signed JWT string
 */
export async function generateAccessToken(claims: UserClaims, config: AuthConfig): Promise<string> {
  const now = Math.floor(Date.now() / 1000);

  const jwt = await new SignJWT({
    userId: claims.userId,
    email: claims.email,
    orgId: claims.orgId,
    permissions: claims.permissions,
    displayName: claims.displayName,
    isSuperAdmin: claims.isSuperAdmin,
    type: "access",
  } satisfies Omit<TokenPayload, "iat" | "exp" | "iss" | "aud">)
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt(now)
    .setExpirationTime(now + config.accessTokenExpiry)
    .setIssuer(TOKEN_ISSUER)
    .setAudience(TOKEN_AUDIENCE)
    .sign(getSecretKey(config.jwtSecret));

  return jwt;
}

/**
 * Generate a refresh token with minimal claims.
 *
 * Only includes userId to minimize exposure if token is compromised.
 *
 * @param claims - User claims (only userId is used)
 * @param config - Auth configuration
 * @returns Signed refresh token string
 */
export async function generateRefreshToken(claims: UserClaims, config: AuthConfig): Promise<string> {
  const now = Math.floor(Date.now() / 1000);

  const jwt = await new SignJWT({
    userId: claims.userId,
    type: "refresh",
  } satisfies Omit<RefreshTokenPayload, "iat" | "exp" | "iss" | "aud">)
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt(now)
    .setExpirationTime(now + config.refreshTokenExpiry)
    .setIssuer(TOKEN_ISSUER)
    .setAudience(TOKEN_AUDIENCE)
    .sign(getSecretKey(config.jwtSecret));

  return jwt;
}

/**
 * Generate both access and refresh tokens in parallel.
 *
 * @param claims - User identity claims
 * @param config - Auth configuration
 * @returns Token pair with access and refresh tokens
 */
export async function generateTokenPair(claims: UserClaims, config: AuthConfig): Promise<TokenPair> {
  const [accessToken, refreshToken] = await Promise.all([
    generateAccessToken(claims, config),
    generateRefreshToken(claims, config),
  ]);

  return { accessToken, refreshToken };
}

/**
 * Verify an access token and extract claims.
 *
 * @param token - JWT string to verify
 * @param config - Auth configuration
 * @returns Verification result with payload or error
 */
export async function verifyToken(token: string, config: AuthConfig): Promise<VerifyResult> {
  try {
    const { payload } = await jwtVerify(token, getSecretKey(config.jwtSecret), {
      issuer: TOKEN_ISSUER,
      audience: TOKEN_AUDIENCE,
    });

    // Validate token type
    if (payload.type !== "access") {
      return {
        valid: false,
        error: "Invalid token type",
      };
    }

    // Extract and validate required claims
    const userId = payload.userId as number | undefined;
    const email = payload.email as string | undefined;
    const permissions = payload.permissions as string[] | undefined;

    if (typeof userId !== "number" || typeof email !== "string" || !Array.isArray(permissions)) {
      return {
        valid: false,
        error: "Invalid token claims",
      };
    }

    return {
      valid: true,
      payload: {
        userId,
        email,
        orgId: payload.orgId as number | undefined,
        permissions,
        displayName: payload.displayName as string | undefined,
        isSuperAdmin: payload.isSuperAdmin as boolean | undefined,
        type: "access",
        iat: payload.iat as number,
        exp: payload.exp as number,
        iss: payload.iss as string,
        aud: payload.aud as string,
      },
    };
  } catch (err) {
    if (err instanceof errors.JWTExpired) {
      return {
        valid: false,
        error: "Token expired",
      };
    }
    if (err instanceof errors.JWTInvalid || err instanceof errors.JWTClaimValidationFailed) {
      return {
        valid: false,
        error: "Invalid token",
      };
    }
    return {
      valid: false,
      error: "Verification failed",
    };
  }
}

/**
 * Verify a refresh token and extract user ID.
 *
 * @param token - Refresh token string to verify
 * @param config - Auth configuration
 * @returns Verification result with userId or error
 */
export async function verifyRefreshToken(token: string, config: AuthConfig): Promise<RefreshVerifyResult> {
  try {
    const { payload } = await jwtVerify(token, getSecretKey(config.jwtSecret), {
      issuer: TOKEN_ISSUER,
      audience: TOKEN_AUDIENCE,
    });

    // Validate token type is refresh
    if (payload.type !== "refresh") {
      return {
        valid: false,
        error: "Invalid token type",
      };
    }

    const userId = payload.userId as number | undefined;
    if (typeof userId !== "number") {
      return {
        valid: false,
        error: "Invalid token claims",
      };
    }

    return {
      valid: true,
      userId,
    };
  } catch (err) {
    if (err instanceof errors.JWTExpired) {
      return {
        valid: false,
        error: "Refresh token expired",
      };
    }
    if (err instanceof errors.JWTInvalid || err instanceof errors.JWTClaimValidationFailed) {
      return {
        valid: false,
        error: "Invalid refresh token",
      };
    }
    return {
      valid: false,
      error: "Verification failed",
    };
  }
}

// ============================================================================
// Unified Token Functions
// ============================================================================

/** Issuer for unified tokens */
const UNIFIED_ISSUER = "auth.animasmarketing.com";

/** Audience for unified tokens */
const UNIFIED_AUDIENCE = "animasmarketing.com";

/**
 * Generate a unified access token with cross-app claims.
 *
 * This is the canonical token format for all Plerity apps. Issued by Portal
 * login, carried in the `animas_auth` HttpOnly cookie on `.animasmarketing.com`.
 *
 * Each token receives a unique `jti` (JWT ID) claim via crypto.randomUUID().
 * The jti is required for server-side revocation: logout writes the jti to
 * audit.revoked_sessions (Postgres mirror) via the internal webhook, and the
 * backend auth middleware rejects any token whose jti appears in that table.
 *
 * @param claims - Unified claims to embed
 * @param secret - JWT signing secret (JWT_SECRET env var)
 * @param expiresInSeconds - Token lifetime in seconds (default: 900 = 15 min)
 * @returns Signed JWT string
 */
export async function generateUnifiedToken(
  claims: UnifiedClaims,
  secret: string,
  expiresInSeconds: number = 900
): Promise<string> {
  const now = Math.floor(Date.now() / 1000);

  const jwt = await new SignJWT({
    sub: claims.sub,
    email: claims.email,
    name: claims.name,
    org_id: claims.org_id,
    org_slug: claims.org_slug,
    role: claims.role,
    entitlements: claims.entitlements,
    is_super_admin: claims.is_super_admin,
    type: "unified_access",
  })
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt(now)
    .setExpirationTime(now + expiresInSeconds)
    .setJti(crypto.randomUUID())
    .setIssuer(UNIFIED_ISSUER)
    .setAudience(UNIFIED_AUDIENCE)
    .sign(getSecretKey(secret));

  return jwt;
}

/**
 * Generate a unified refresh token (minimal claims for security).
 *
 * Each token receives a unique `jti` so logout can revoke it server-side
 * (portal D1 `revoked_sessions`, checked by both refresh endpoints). Refresh
 * tokens issued before the jti was added carry none and stay valid until their
 * natural expiry (they cannot be revoked individually).
 *
 * @param userId - User ID (stored as `sub`)
 * @param orgId - Current org ID
 * @param secret - JWT signing secret
 * @param expiresInSeconds - Token lifetime in seconds (default: 604800 = 7 days)
 * @returns Signed refresh JWT string
 */
export async function generateUnifiedRefreshToken(
  userId: string,
  orgId: number | null,
  secret: string,
  expiresInSeconds: number = 604800
): Promise<string> {
  const now = Math.floor(Date.now() / 1000);

  const jwt = await new SignJWT({
    sub: userId,
    org_id: orgId,
    type: "unified_refresh",
  })
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt(now)
    .setExpirationTime(now + expiresInSeconds)
    .setJti(crypto.randomUUID())
    .setIssuer(UNIFIED_ISSUER)
    .setAudience(UNIFIED_AUDIENCE)
    .sign(getSecretKey(secret));

  return jwt;
}

/**
 * Verify and parse a unified access token.
 *
 * Validates signature, issuer, audience, expiry, and token type.
 * Use this server-side (Portal API routes, backend servers).
 *
 * @param token - JWT string to verify
 * @param secret - JWT signing secret
 * @returns Parsed UnifiedClaims or null if invalid
 */
export async function parseUnifiedToken(token: string, secret: string): Promise<UnifiedClaims | null> {
  try {
    const { payload } = await jwtVerify(token, getSecretKey(secret), {
      issuer: UNIFIED_ISSUER,
      audience: UNIFIED_AUDIENCE,
    });

    // Validate token type
    if (payload.type !== "unified_access") {
      return null;
    }

    // Validate required claims
    const sub = payload.sub as string | undefined;
    const email = payload.email as string | undefined;
    if (typeof sub !== "string" || typeof email !== "string") {
      return null;
    }

    return {
      sub,
      email,
      name: (payload.name as string) ?? email.split("@")[0],
      org_id: (payload.org_id as number) ?? null,
      org_slug: (payload.org_slug as string) ?? null,
      role: (payload.role as string) ?? "member",
      entitlements: Array.isArray(payload.entitlements) ? (payload.entitlements as string[]) : [],
      is_super_admin: Boolean(payload.is_super_admin),
    };
  } catch {
    return null;
  }
}

/**
 * Verify a unified refresh token.
 *
 * @param token - Refresh token string
 * @param secret - JWT signing secret
 * @returns Object with userId, orgId, and the token's `jti` / `exp` (null for a
 *   pre-jti legacy refresh token), or null if invalid. The caller owns the
 *   revocation check — this helper is signature/claims only.
 */
export async function parseUnifiedRefreshToken(
  token: string,
  secret: string
): Promise<{
  userId: string;
  orgId: number | null;
  jti: string | null;
  exp: number | null;
  iat: number | null;
} | null> {
  try {
    const { payload } = await jwtVerify(token, getSecretKey(secret), {
      issuer: UNIFIED_ISSUER,
      audience: UNIFIED_AUDIENCE,
    });

    if (payload.type !== "unified_refresh") {
      return null;
    }

    const sub = payload.sub as string | undefined;
    if (typeof sub !== "string") {
      return null;
    }

    return {
      userId: sub,
      orgId: (payload.org_id as number) ?? null,
      jti: typeof payload.jti === "string" ? payload.jti : null,
      exp: typeof payload.exp === "number" ? payload.exp : null,
      // `iat` lets the caller enforce a per-user token epoch (portal security
      // audit E-4, 2026-10-30: credential changes revoke older refresh tokens).
      iat: typeof payload.iat === "number" ? payload.iat : null,
    };
  } catch {
    return null;
  }
}

/**
 * Extract the expiry timestamp (exp) from a JWT without signature verification.
 *
 * Use client-side to schedule token refresh before expiry.
 * NEVER use for authorization decisions — always verify server-side.
 *
 * @param token - JWT string
 * @returns Unix timestamp in seconds, or null if malformed/missing
 */
export function getUnifiedTokenExpiry(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payloadB64 = parts[1];
    const padded = payloadB64 + "=".repeat((4 - (payloadB64.length % 4)) % 4);
    const payloadJson = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson);
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Decode a unified token WITHOUT signature verification.
 *
 * Use client-side ONLY for non-sensitive display purposes (showing user name,
 * org, etc. in the UI). The animas_auth cookie is readable by JS (not HttpOnly)
 * so AuthProvider can decode it directly without a cross-origin API call.
 *
 * NEVER use this for authorization decisions — always verify server-side.
 *
 * @param token - JWT string (from cookie or header)
 * @returns AuthUser for UI display, or null if malformed
 */
export function decodeUnifiedTokenUnsafe(token: string): AuthUser | null {
  try {
    // JWT format: header.payload.signature — decode middle part
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Base64url decode the payload
    const payloadB64 = parts[1];
    const padded = payloadB64 + "=".repeat((4 - (payloadB64.length % 4)) % 4);
    const payloadJson = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson);

    // Check if this is a unified token
    if (payload.type !== "unified_access") return null;

    // Check expiry
    const now = Math.floor(Date.now() / 1000);
    if (typeof payload.exp === "number" && payload.exp < now) return null;

    const sub = payload.sub;
    if (typeof sub !== "string") return null;

    return {
      userId: parseInt(sub, 10),
      email: payload.email ?? "",
      name: payload.name ?? payload.email?.split("@")[0] ?? "",
      orgId: typeof payload.org_id === "number" ? payload.org_id : null,
      orgSlug: typeof payload.org_slug === "string" ? payload.org_slug : null,
      role: payload.role ?? "member",
      entitlements: Array.isArray(payload.entitlements) ? payload.entitlements : [],
      isSuperAdmin: Boolean(payload.is_super_admin),
      brandColor: typeof payload.brand_color === "string" ? payload.brand_color : null,
    };
  } catch {
    return null;
  }
}
